import { CrescLoader } from "../components/LoadingScreen";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useEntitlements } from "../context/EntitlementsContext";
import { toast } from "sonner";

/**
 * BillingSuccess — runs after Stripe redirects back from a successful
 * checkout. Two flows:
 *   1) Sandbox / emergentintegrations checkout — `?session_id={...}` is
 *      present. Poll `/api/payments/checkout/status/{id}` until `paid`.
 *   2) Stripe Payment Link redirect (real account) — no session_id is
 *      present (Payment Links don't expose the session id in the
 *      success URL). Poll `/api/me/entitlements` until the user's plan
 *      flips. The plan flip happens via the webhook (when configured)
 *      or as a follow-up reconciliation.
 *
 * Both flows time out at ~60 seconds with a friendly message.
 */
export default function BillingSuccess() {
  const [params] = useSearchParams();
  const ent = useEntitlements();
  const nav = useNavigate();
  const [status, setStatus] = useState("polling");
  const sessionId = params.get("session_id");
  // Capture the user's plan AT MOUNT — the entitlements-poll path waits
  // for it to change. If the user was already on Pro and just bought
  // Business, we want to detect that flip.
  const [startPlan] = useState(ent.plan);

  useEffect(() => {
    let attempts = 0;
    let timer = null;

    const pollSession = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await ent.reload();
          toast.success(`You're on the ${data.plan_id?.toUpperCase()} plan`);
          setTimeout(() => nav("/app"), 1500);
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 12) { setStatus("timeout"); return; }
        timer = setTimeout(pollSession, 5000);
      } catch {
        if (attempts >= 12) { setStatus("error"); return; }
        timer = setTimeout(pollSession, 5000);
      }
    };

    const pollEntitlements = async () => {
      attempts++;
      try {
        const { data } = await api.get("/me/entitlements");
        if (data.plan && data.plan !== "free" && data.plan !== startPlan) {
          setStatus("paid");
          await ent.reload();
          toast.success(`You're on the ${data.plan?.toUpperCase()} plan`);
          setTimeout(() => nav("/app"), 1500);
          return;
        }
        if (attempts >= 30) { setStatus("timeout"); return; }
        // Faster polling at first (every 2s for 10s, then 5s).
        timer = setTimeout(pollEntitlements, attempts < 5 ? 2000 : 5000);
      } catch {
        if (attempts >= 30) { setStatus("error"); return; }
        timer = setTimeout(pollEntitlements, 5000);
      }
    };

    if (sessionId) pollSession();
    else pollEntitlements();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] flex items-center justify-center">
      <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-8 max-w-md w-full mx-4 text-center" data-testid="billing-success-page">
        {status === "polling" && (
          <>
            <CrescLoader size={40} color="#5A90F5" />
            <h1 className="font-display text-lg mb-1">Confirming your payment…</h1>
            <p className="text-xs text-[#A1A1AA]">
              {sessionId
                ? "This usually takes a few seconds."
                : "Stripe is finalizing your subscription. Hang tight."}
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-[#34C759] mx-auto mb-3" />
            <h1 className="font-display text-lg mb-1">Payment confirmed</h1>
            <p className="text-xs text-[#A1A1AA]">Redirecting you back to the app…</p>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <XCircle className="w-10 h-10 text-[#FF3B30] mx-auto mb-3" />
            <h1 className="font-display text-lg mb-1">
              {status === "expired" ? "Checkout expired" : status === "timeout" ? "Still processing" : "Couldn't confirm"}
            </h1>
            <p className="text-xs text-[#A1A1AA] mb-4">
              {status === "timeout"
                ? "Your payment may still complete. Refresh this page in a minute or check Settings → Billing."
                : "Try the upgrade again from Settings → Billing."}
            </p>
            <button
              onClick={() => nav("/app")}
              className="px-4 py-2 rounded-md bg-[#5A90F5] text-white text-sm hover:bg-[#4a80e5]"
            >
              Back to app
            </button>
          </>
        )}
      </div>
    </div>
  );
}
